import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { Icon, Loading } from '@ibk/shared-ui';
import { formatDate, getDateWithoutTimezone } from '@ibk/shared-utils';
import { useGetRechargeHistoryQuery } from '../../services/benefitApi';
import { ProtocolSearch } from '../ProtocolSearch';
import { RechargeHistoryList } from '../RechargeHistoryList';
import styles from './RechargeHistoryContainer.module.css';
const SEARCH_DEBOUNCE_MS = 300;
export const RechargeHistoryContainer = ({ contractId, onRechargeClick, 'data-testid': testId, }) => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchQuery]);
    const { data: historyData, isLoading: isLoadingHistory, isFetching: isFetchingHistory, error: historyError, } = useGetRechargeHistoryQuery({
        contractId,
        limit: 100,
        ...(debouncedSearch && { search: debouncedSearch }),
    });
    const recharges = useMemo(() => historyData?.result ?? [], [historyData]);
    const groupedRecharges = useMemo(() => {
        if (recharges.length === 0)
            return [];
        const groups = new Map();
        recharges.forEach(recharge => {
            const rechargeDate = getDateWithoutTimezone(recharge.createdAt);
            const month = rechargeDate.getMonth() + 1;
            const year = rechargeDate.getFullYear();
            const competenceKey = `${month.toString().padStart(2, '0')}/${year}`;
            const { month: competenceDisplay } = formatDate(rechargeDate);
            const monthCapitalized = competenceDisplay.charAt(0).toUpperCase() + competenceDisplay.slice(1);
            if (!groups.has(competenceKey)) {
                groups.set(competenceKey, {
                    competence: monthCapitalized,
                    competenceKey,
                    recharges: [],
                });
            }
            groups.get(competenceKey).recharges.push(recharge);
        });
        return Array.from(groups.values())
            .sort((a, b) => {
            const [aMonth, aYear] = a.competenceKey.split('/').map(Number);
            const [bMonth, bYear] = b.competenceKey.split('/').map(Number);
            if (aYear !== bYear)
                return bYear - aYear;
            return bMonth - aMonth;
        })
            .map(group => {
            const sortedRecharges = group.recharges.toSorted((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            return {
                ...group,
                recharges: sortedRecharges,
            };
        });
    }, [recharges]);
    const handleSearchChange = useCallback((value) => {
        setSearchQuery(value);
    }, []);
    const handleClearSearch = useCallback(() => {
        setSearchQuery('');
        setDebouncedSearch('');
    }, []);
    const isActiveSearch = debouncedSearch.length > 0;
    const isInitialLoading = isLoadingHistory && recharges.length === 0 && !isActiveSearch;
    const hasError = !!historyError;
    const isSearchLoading = isFetchingHistory && isActiveSearch;
    const isSearchNotFound = isActiveSearch && recharges.length === 0 && !isFetchingHistory && !hasError;
    return (_jsxs("div", { className: styles.container, "data-testid": testId, children: [_jsx("div", { className: styles.searchSection, children: _jsx(ProtocolSearch, { value: searchQuery, onChange: handleSearchChange, onClear: handleClearSearch, isLoading: isSearchLoading, placeholder: t('benefits.recharge-history.search.placeholder'), "data-testid": `div${testId}Search` }) }), _jsxs("div", { className: styles.contentSection, children: [isInitialLoading && (_jsxs("div", { className: styles.loadingContainer, "data-testid": `div${testId}Loading`, children: [_jsx(Loading, {}), _jsx("p", { className: styles.loadingText, children: t('benefits.recharge-history.loading') })] })), isSearchLoading && (_jsxs("div", { className: styles.errorContainer, "data-testid": `div${testId}SearchLoading`, children: [_jsx("div", { className: styles.errorIcon, children: _jsx(Loading, { size: 'micro' }) }), _jsx("h3", { className: styles.errorTitle, children: t('benefits.recharge-history.loading.title') }), _jsx("p", { className: styles.errorDescription, children: t('benefits.recharge-history.loading.description') })] })), isSearchNotFound && !isSearchLoading && (_jsxs("div", { className: styles.errorContainer, "data-testid": `div${testId}SearchNotFound`, children: [_jsx("div", { className: styles.errorIcon, children: _jsx(Icon, { name: 'circleError', className: styles.errorIconInner }) }), _jsx("h3", { className: styles.errorTitle, children: t('benefits.recharge-history.search.not-found.title') }), _jsx("p", { className: styles.errorDescription, children: t('benefits.recharge-history.search.not-found.description') })] })), !isInitialLoading &&
                        !isSearchNotFound &&
                        !isSearchLoading &&
                        (groupedRecharges.length === 0 || hasError) &&
                        !isActiveSearch && (_jsxs("div", { className: styles.emptyContainer, "data-testid": `div${testId}Empty`, children: [_jsx("div", { className: styles.emptyIcon, children: _jsx(Icon, { name: 'recharge', className: styles.emptyIconText }) }), _jsx("h3", { className: styles.emptyTitle, children: t('benefits.recharge-history.empty.title') }), _jsx("p", { className: styles.emptyDescription, children: t('benefits.recharge-history.empty.description') })] })), !isInitialLoading && !hasError && !isSearchLoading && groupedRecharges.length > 0 && (_jsx(RechargeHistoryList, { groups: groupedRecharges, onRechargeClick: onRechargeClick, "data-testid": `${testId}List` }))] })] }));
};
